import React, { useState } from "react";
import { Layers, Check, X } from "lucide-react";
import {
  DEFAULT_PLATFORMS_LIST,
  DEFAULT_CMP_LIST,
} from "../../config/constants";
import { useCase } from "../../context/CaseContext";

const ScopeConfiguration: React.FC = () => {
  const {
    platforms,
    setPlatforms,
    cmps,
    setCmps,
    customPlatforms,
    setCustomPlatforms,
    customCmps,
    setCustomCmps,
    customScope,
    setCustomScope,
    customAdvancedScopes,
    setCustomAdvancedScopes,
    savePartial,
    logAuditAction,
    currentCaseId,
    clientName,
    user,
  } = useCase();

  const [newCustomPlatform, setNewCustomPlatform] = useState("");
  const [newCustomCmp, setNewCustomCmp] = useState("");
  const [newCustomAdvancedScope, setNewCustomAdvancedScope] = useState("");

  const addCustomPlatform = () => {
    if (!newCustomPlatform) return;
    const updated = [...customPlatforms, newCustomPlatform];
    setCustomPlatforms(updated);
    setPlatforms({ ...platforms, [newCustomPlatform]: true });
    savePartial({
      customPlatforms: updated,
      [`platforms.${newCustomPlatform}`]: true,
    });
    setNewCustomPlatform("");
  };

  const removeCustomPlatform = (p: string) => {
    if (!window.confirm(`Delete ${p}?`)) return;
    const updated = customPlatforms.filter((i) => i !== p);
    setCustomPlatforms(updated);
    const newPlatforms = { ...platforms };
    delete newPlatforms[p];
    setPlatforms(newPlatforms);
    savePartial({ customPlatforms: updated, platforms: newPlatforms });
  };

  const addCustomCmp = () => {
    if (!newCustomCmp) return;
    const updated = [...customCmps, newCustomCmp];
    setCustomCmps(updated);
    setCmps({ ...cmps, [newCustomCmp]: true });
    savePartial({ customCmps: updated, [`cmps.${newCustomCmp}`]: true });
    setNewCustomCmp("");
  };

  const removeCustomCmp = (c: string) => {
    if (!window.confirm(`Delete ${c}?`)) return;
    const updated = customCmps.filter((i) => i !== c);
    setCustomCmps(updated);
    const newCmps = { ...cmps };
    delete newCmps[c];
    setCmps(newCmps);
    savePartial({ customCmps: updated, cmps: newCmps });
  };

  const addCustomAdvancedScope = () => {
    if (!newCustomAdvancedScope) return;
    const updated = [...customAdvancedScopes, newCustomAdvancedScope];
    setCustomAdvancedScopes(updated);
    setCustomScope({ ...customScope, [newCustomAdvancedScope]: true });
    savePartial({
      customAdvancedScopes: updated,
      [`customScope.${newCustomAdvancedScope}`]: true,
    });
    setNewCustomAdvancedScope("");
  };

  const removeCustomAdvancedScope = (s: string) => {
    if (!window.confirm(`Delete ${s}?`)) return;
    const updated = customAdvancedScopes.filter((i) => i !== s);
    setCustomAdvancedScopes(updated);
    const newScope = { ...customScope };
    delete newScope[s];
    setCustomScope(newScope);
    savePartial({ customAdvancedScopes: updated, customScope: newScope });
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
      <div className="bg-slate-50 dark:bg-slate-900/50 px-5 py-4 border-b border-gray-200 dark:border-slate-700">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <Layers size={14} /> Scope & Configuration
        </h3>
      </div>
      <div className="p-6 space-y-8">
        {/* PLATFORMS */}
        <div>
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            Detected Platforms
          </h4>
          <div className="flex flex-wrap gap-2">
            {[
              ...DEFAULT_PLATFORMS_LIST,
              ...customPlatforms.map((l) => ({ id: l, label: l })),
            ].map((p) => {
              const isCustom = customPlatforms.includes(p.id);
              return (
                <div key={p.id} className="relative group">
                  <button
                    onClick={() => {
                      const v = !platforms[p.id];
                      setPlatforms({ ...platforms, [p.id]: v });
                      savePartial({ [`platforms.${p.id}`]: v });
                      logAuditAction(
                        "UPDATE",
                        {
                          collection: "onboarding_cases",
                          id: currentCaseId || "",
                          name: clientName || "Unknown",
                        },
                        {
                          uid: user?.uid || "unknown",
                          email: user?.email || "unknown",
                        },
                        [
                          {
                            field: `platform_${p.id}`,
                            oldValue: !v,
                            newValue: v,
                          },
                        ],
                      );
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold border transition-all flex items-center gap-2 ${platforms[p.id] ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-gray-200 dark:border-slate-600 hover:border-blue-300"}`}
                  >
                    {platforms[p.id] && <Check size={14} />} {p.label}
                  </button>
                  {/* X for Deletion (Custom Only) */}
                  {isCustom && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustomPlatform(p.id);
                      }}
                      className="absolute -top-1 -right-1 bg-white border border-gray-200 rounded-full p-0.5 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 shadow-sm z-10 transition-opacity"
                      title="Delete Platform"
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>
              );
            })}
            <input
              className="px-3 py-1.5 text-xs border border-gray-200 dark:border-slate-700 rounded-full bg-transparent dark:text-white outline-none focus:border-blue-500 transition-all placeholder:text-gray-400 w-28 focus:w-40"
              placeholder="+ Custom..."
              value={newCustomPlatform}
              onChange={(e) => setNewCustomPlatform(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addCustomPlatform();
              }}
            />
          </div>
        </div>

        {/* CMP */}
        <div>
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            CMP / Cookie Banner
          </h4>
          <div className="flex flex-wrap gap-2">
            {[
              ...DEFAULT_CMP_LIST,
              ...customCmps.map((l) => ({ id: l, label: l })),
            ].map((p) => {
              const isCustom = customCmps.includes(p.id);
              return (
                <div key={p.id} className="relative group">
                  <button
                    onClick={() => {
                      const v = !cmps[p.id];
                      setCmps({ ...cmps, [p.id]: v });
                      savePartial({ [`cmps.${p.id}`]: v });
                      logAuditAction(
                        "UPDATE",
                        {
                          collection: "onboarding_cases",
                          id: currentCaseId || "",
                          name: clientName || "Unknown",
                        },
                        {
                          uid: user?.uid || "unknown",
                          email: user?.email || "unknown",
                        },
                        [
                          {
                            field: `cmp_${p.id}`,
                            oldValue: !v,
                            newValue: v,
                          },
                        ],
                      );
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold border transition-all flex items-center gap-2 ${cmps[p.id] ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-gray-200 dark:border-slate-600 hover:border-blue-300"}`}
                  >
                    {cmps[p.id] && <Check size={14} />} {p.label}
                  </button>
                  {/* X for Deletion (Custom Only) */}
                  {isCustom && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustomCmp(p.id);
                      }}
                      className="absolute -top-1 -right-1 bg-white border border-gray-200 rounded-full p-0.5 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 shadow-sm z-10 transition-opacity"
                      title="Delete CMP"
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>
              );
            })}
            <input
              className="px-3 py-1.5 text-xs border border-gray-200 dark:border-slate-700 rounded-full bg-transparent dark:text-white outline-none focus:border-blue-500 transition-all placeholder:text-gray-400 w-28 focus:w-40"
              placeholder="+ Custom..."
              value={newCustomCmp}
              onChange={(e) => setNewCustomCmp(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addCustomCmp();
              }}
            />
          </div>
        </div>

        {/* ADVANCED SCOPE */}
        <div>
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            Advanced Scope
          </h4>
          <div className="flex flex-wrap gap-2">
            {[
              ...["datalayers", "offline_conv"].map((id) => ({
                id,
                label: id === "offline_conv" ? "Offline Conv" : "Datalayers",
              })),
              ...customAdvancedScopes.map((l) => ({ id: l, label: l })),
            ].map((s) => {
              const isCustom = customAdvancedScopes.includes(s.id);
              return (
                <div key={s.id} className="relative group">
                  <button
                    onClick={() => {
                      const v = !customScope[s.id];
                      setCustomScope({ ...customScope, [s.id]: v });
                      savePartial({ [`customScope.${s.id}`]: v });
                      logAuditAction(
                        "UPDATE",
                        {
                          collection: "onboarding_cases",
                          id: currentCaseId || "",
                          name: clientName || "Unknown",
                        },
                        {
                          uid: user?.uid || "unknown",
                          email: user?.email || "unknown",
                        },
                        [
                          {
                            field: `scope_${s.id}`,
                            oldValue: !v,
                            newValue: v,
                          },
                        ],
                      );
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold border transition-all ${customScope[s.id] ? "bg-slate-700 text-white border-slate-700 dark:bg-slate-600" : "bg-white dark:bg-slate-900 text-gray-400 border-gray-200 dark:border-slate-600 hover:border-slate-400"}`}
                  >
                    {s.label}
                  </button>
                  {/* X for Deletion (Custom Only) */}
                  {isCustom && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustomAdvancedScope(s.id);
                      }}
                      className="absolute -top-1 -right-1 bg-white border border-gray-200 rounded-full p-0.5 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 shadow-sm z-10 transition-opacity"
                      title="Delete Scope"
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>
              );
            })}
            <input
              className="px-3 py-1.5 text-xs border border-gray-200 dark:border-slate-700 rounded-full bg-transparent dark:text-white outline-none focus:border-blue-500 transition-all placeholder:text-gray-400 w-28 focus:w-40"
              placeholder="+ Custom..."
              value={newCustomAdvancedScope}
              onChange={(e) => setNewCustomAdvancedScope(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addCustomAdvancedScope();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScopeConfiguration;
